import React, { useEffect, useMemo, useRef, useState } from "react";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import {
  InstantSearch,
  Configure,
  useRefinementList,
  useStats,
} from "react-instantsearch-hooks";
import { useSelector } from "react-redux";
import config from "@/config";
import CanView from "../../components/CanView.jsx";
import ProductHits from "./hits";
import Autocomplete from "./autocomplete";
import { PrimaryButton } from "../../components/Buttons.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import DropdownEditMenu from "../../components/DropdownEditMenu.jsx";
import Loader from "../../components/Loader.jsx";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import { FaCrown } from "react-icons/fa6";
import { forceSyncTypesense } from "@/api/admin.js";
import DocumentModal from "@/components/DocumentModal.jsx";
import ProductImageModal from "@/components/ProductImageModal.jsx";

const CustomStates = React.memo(() => {
  const { nbHits } = useStats();
  return <>{nbHits}</>;
});

const CustomRefinementList = React.memo(
  ({
    view,
    disableNav,
    publishedOnly,
    setPublishedOnly,
    discountedOnly,
    setDiscountedOnly,
  }) => {
    const searchBox = useRef(null);
    const {
      items: categoryItems,
      refine: categoryRefine,
      toggleShowMore: categoryToggleShowMore,
      hasExhaustiveItems: categoryCanToggleShowMore,
      isShowingMore: categoryIsShowingMore,
    } = useRefinementList({
      attribute: "default_category",
      sortBy: ["count"],
      limit: 10,
      showMoreLimit: 500,
      showMore: true,
    });

    const {
      items: brandItems,
      refine: brandRefine,
      toggleShowMore: brandToggleShowMore,
      hasExhaustiveItems: brandCanToggleShowMore,
      isShowingMore: brandIsShowingMore,
      searchForItems,
    } = useRefinementList({
      attribute: "company",
      sortBy: ["count"],
      limit: 10,
      showMoreLimit: 1000,
      showMore: true,
    });

    return (
      <div className="col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
        <div className="flex flex-col h-full p-5">
          <header>
            <div className="flex items-center justify-between font-bold">
              Published Only{" "}
              <ToggleSwitch
                onChange={(v) => setPublishedOnly(v)}
                checked={publishedOnly}
              />
            </div>
          </header>
        </div>
        <div className="flex flex-col h-full p-5">
          <header>
            <div className="flex items-center justify-between font-bold">
              Discounted Only{" "}
              <ToggleSwitch
                onChange={(v) => setDiscountedOnly(v)}
                checked={discountedOnly}
              />
            </div>
          </header>
        </div>
        <div className="flex flex-col h-full p-5">
          <header>
            <div className="flex items-center justify-between font-bold">
              {view === "allProducts" ? "Saucey Categories" : "Categories"}
            </div>
          </header>
          <div className="grow mt-2">
            {categoryItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between py-2"
              >
                <input
                  disabled={disableNav}
                  type="checkbox"
                  id={item.label}
                  name={item.label}
                  value={item.label}
                  checked={item.isRefined}
                  onChange={() => categoryRefine(item.value)}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                />
                <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                  {item.label}
                </span>
                <div className="text-sm font-medium text-slate-500">
                  {item.count}
                </div>
              </div>
            ))}
            {(!categoryCanToggleShowMore || categoryIsShowingMore) && (
              <button
                onClick={categoryToggleShowMore}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:hover:text-white"
              >
                {categoryIsShowingMore ? "Show Less" : "Show More"}
              </button>
            )}
          </div>

          <header>
            <div className="flex items-center justify-between mt-8 font-bold">
              Brands
            </div>
          </header>
          <div className="grow mt-2">
            <input
              className="form-input w-full"
              type="search"
              placeholder={"Search Brands..."}
              ref={searchBox}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              maxLength={512}
              onChange={(event) => searchForItems(event.currentTarget.value)}
            />
            {brandItems.map((item) => (
              <div
                key={item.label}
                className="flex items-center justify-between py-2"
              >
                <input
                  disabled={disableNav}
                  type="checkbox"
                  id={item.label}
                  name={item.label}
                  value={item.label}
                  checked={item.isRefined}
                  onChange={() => brandRefine(item.value)}
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                />
                <span className="text-sm font-medium text-slate-500 hover:text-slate-800 dark:hover:text-white flex flex-grow ml-3">
                  {item.label}
                </span>
                <div className="text-sm font-medium text-slate-500">
                  {item.count}
                </div>
              </div>
            ))}
            {(!brandCanToggleShowMore || brandIsShowingMore) &&
              !searchBox.current?.value && (
                <button
                  onClick={brandToggleShowMore}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-800 dark:hover:text-white"
                >
                  {brandIsShowingMore ? "Show Less" : "Show More"}
                </button>
              )}
          </div>
        </div>
      </div>
    );
  }
);

const ProductsListing = () => {
  const [searchClient, setSearchClient] = useState(null);
  const [publishedOnly, setPublishedOnly] = useState(false);
  const [discountedOnly, setDiscountedOnly] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [searchClientWithoutGrouping, setSearchClientWithoutGrouping] =
    useState(null);
  const [documentModalOpen, setDocumentModalOpen] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(null);

  const navigate = useNavigate();
  const productHitsRef = useRef();
  const urlParams = new URLSearchParams(window.location.search);
  const view = urlParams.get("view");
  // if view is there, remove it from the url
  if (view) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  const location = useLocation();

  useEffect(() => {
    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.ts.key,
        nodes: config.ts.nodes,
        nearestNode: config.ts.nearestNode,
        cacheSearchResultsForSeconds: 0,
      },
      additionalSearchParameters: {
        query_by: "title,subtitle,description,default_category,company",
        groupBy: "sku_id",
        groupLimit: 99,
      },
    });
    const typesenseInstantsearchAdapterWithoutGroupoing =
      new TypesenseInstantSearchAdapter({
        server: {
          apiKey: config.ts.key,
          nodes: config.ts.nodes,
          nearestNode: config.ts.nearestNode,
          cacheSearchResultsForSeconds: 0,
        },
        additionalSearchParameters: {
          query_by: "company,title,subtitle,description,default_category",
        },
      });
    const searchClient = typesenseInstantsearchAdapter.searchClient;
    const searchClientWithoutGrouping =
      typesenseInstantsearchAdapterWithoutGroupoing.searchClient;
    setSearchClient(searchClient);
    setSearchClientWithoutGrouping(searchClientWithoutGrouping);
  }, []);
  const [currentView, setCurrentView] = useState(view || "myProducts");
  const { selectedMerchant, selectedLocation, currentRole, user } = useSelector(
    (state) => state.app
  );

  const [disableNav, setDisableNav] = useState(false);
  const [selectedSkuCount, setSelectedSkuCount] = useState(0);
  useEffect(() => {
    setSynced(false);
    setSyncing(false);
  }, [selectedMerchant]);
  const headers =
    currentRole.includes("admin") ||
    currentRole.includes("manager") ||
    user.type === "saucey_admin"
      ? [
          "Product",
          "Brand",
          currentView === "allProducts" ? "Saucey Category" : "Category",
          "Cost",
          "Price",
          "Published",
          "",
        ]
      : [
          "Product",
          "Brand",
          currentView === "allProducts" ? "Saucey Category" : "Category",
          "Cost",
          "Price",
          "Published",
          "",
        ];

  const { indexName, filter, viewName } = useMemo(() => {
    if (!selectedLocation) return { filter: "", headers: [""] };
    productHitsRef.current?.bulkEdit("clear-selected");
    let filter = [];
    let indexName = "";
    let viewName = "";

    console.log(selectedLocation);
    if (currentView === "myProducts") {
      indexName = `${config.environment}_${selectedMerchant.easy_id}_products`;
      filter.push(`${selectedLocation.store_id}_price:>=0`);
      viewName = "My Inventory";
    } else if (currentView === "custom") {
      indexName = `${config.environment}_${selectedMerchant.easy_id}_products`;
      filter.push(`is_custom:=true`);
      viewName = "My Custom Products";
    } else {
      viewName = "All Products";
      if (
        selectedLocation.tobacco_enabled &&
        selectedLocation.alcohol_enabled
      ) {
        if (!["nmi", "frame"].includes(selectedLocation.payment_gateway)) {
          filter.push("is_high_risk: false");
        }
      } else if (
        selectedLocation.tobacco_enabled &&
        !selectedLocation.alcohol_enabled
      ) {
        if (!["nmi", "frame"].includes(selectedLocation.payment_gateway)) {
          filter.push("(is_tobacco: true && is_high_risk: false)");
        } else {
          filter.push("(is_tobacco: true || is_high_risk: true)");
        }
      } else if (
        selectedLocation.alcohol_enabled &&
        !selectedLocation.tobacco_enabled
      ) {
        if (!["nmi", "frame"].includes(selectedLocation.payment_gateway)) {
          filter.push("(is_tobacco:!= true && is_high_risk: false)");
        } else {
          filter.push("(is_tobacco:!= true || is_high_risk: true)");
        }
      } else {
        filter.push("base_category: NOTENABLED");
      }
    }
    if (publishedOnly) {
      filter.push(`${selectedLocation.store_id}_qty:>0`);
    }
    if (discountedOnly) {
      filter.push(`${selectedLocation.store_id}_discounted_price:>0`);
    }
    return { filter: filter.join(" && "), viewName, indexName };
  }, [
    currentView,
    selectedLocation,
    productHitsRef,
    publishedOnly,
    discountedOnly,
  ]);

  const selectedClass = useMemo(() => {
    let classname =
      "inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-transparent shadow-sm bg-theme-button-primary text-theme-button-primary-text duration-150 ease-in-out";
    if (disableNav) {
      classname += " grayscale";
    }
    return classname;
  }, [disableNav]);

  const unselectedClass = useMemo(() => {
    let classname =
      "inline-flex items-center justify-center text-sm font-medium leading-5 rounded-full px-3 py-1 border border-theme-button-primary hover:bg-theme-button-secondary-hover shadow-sm bg-transparent text-theme-button-primary duration-150 ease-in-out";
    if (disableNav) {
      classname += " grayscale";
    }
    return classname;
  }, [disableNav]);

  if (!selectedLocation) {
    return null;
  }

  if (!searchClient) {
    return <Loader />;
  }

  return (
    <InstantSearch
      indexName={indexName || config.ts.index}
      searchClient={
        currentView === "allProducts"
          ? searchClientWithoutGrouping
          : searchClient
      }
    >
      <Configure hitsPerPage={50} filters={filter} />

      <div className="px-4 sm:px-6 lg:px-8 py-8 md:py-0 w-full max-w-9xl mx-auto overflow-x-auto">
        <div className="xl:flex">
          <div className="md:flex flex-1">
            <div className="w-full md:w-80 mb-8 md:mb-0">
              <div className="md:sticky  md:h-[calc(100dvh-64px)] md:overflow-x-hidden md:overflow-y-auto no-scrollbar ">
                <div className="md:py-8">
                  <header className="mb-6">
                    <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                      Products
                    </h1>
                  </header>
                  <CustomRefinementList
                    publishedOnly={publishedOnly}
                    setPublishedOnly={setPublishedOnly}
                    discountedOnly={discountedOnly}
                    setDiscountedOnly={setDiscountedOnly}
                    view={currentView}
                    disableNav={disableNav}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 md:ml-8 xl:mx-4 2xl:mx-8 pt-[18px]">
              <div className="md:py-8">
                <div className="space-y-4">
                  <CanView permissions={"manager"}>
                    <div className="flex items-center">
                      <ul className="flex flex-wrap -m-1">
                        <li className="m-1">
                          <button
                            disabled={disableNav}
                            className={
                              currentView === "myProducts"
                                ? selectedClass
                                : unselectedClass
                            }
                            onClick={() => setCurrentView("myProducts")}
                          >
                            My Inventory
                          </button>
                        </li>
                        <li className="m-1">
                          <button
                            disabled={disableNav}
                            className={
                              currentView === "allProducts"
                                ? selectedClass
                                : unselectedClass
                            }
                            onClick={() => setCurrentView("allProducts")}
                          >
                            All Verified Products
                          </button>
                        </li>

                        <li className="m-1">
                          <button
                            disabled={disableNav}
                            className={
                              currentView === "custom"
                                ? selectedClass
                                : unselectedClass
                            }
                            onClick={() => setCurrentView("custom")}
                          >
                            My Custom Products
                          </button>
                        </li>
                        <CanView permissions="saucey_admin">
                          <li className="m-1">
                            <PrimaryButton
                              loading={syncing}
                              success={synced}
                              size="sm"
                              onClick={async () => {
                                setSyncing(true);
                                try {
                                  await forceSyncTypesense(
                                    selectedMerchant.easy_id
                                  );
                                } catch (e) {
                                } finally {
                                  setSyncing(false);
                                  setSynced(true);
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                Force TS Sync <FaCrown />
                              </div>
                            </PrimaryButton>
                          </li>
                        </CanView>
                      </ul>
                      <CanView permissions="manager">
                        <div className={"flex-grow justify-end flex gap-2"}>
                          <div>
                            <DropdownEditMenu
                              align="right"
                              className="relative inline-flex"
                              buttonText={
                                <div className="flex items-center">
                                  Bulk Edit{" "}
                                  <svg
                                    className="w-8 fill-current"
                                    viewBox="0 0 32 16"
                                  >
                                    <circle cx="16" cy="8" r="2" />
                                    <circle cx="10" cy="8" r="2" />
                                    <circle cx="22" cy="8" r="2" />
                                  </svg>
                                </div>
                              }
                              disabled={selectedSkuCount === 0}
                            >
                              {currentView !== "allProducts" && (
                                <li>
                                  <button
                                    onClick={() => {
                                      productHitsRef.current.bulkEdit(
                                        "assign-categories"
                                      );
                                    }}
                                    className="font-medium text-sm flex py-1 px-3"
                                  >
                                    Assign Categories
                                  </button>
                                </li>
                              )}
                              {currentView !== "allProducts" && (
                                <li>
                                  <button
                                    onClick={() => {
                                      productHitsRef.current.bulkEdit(
                                        "edit-price"
                                      );
                                    }}
                                    className="font-medium text-sm flex py-1 px-3"
                                  >
                                    Edit Price
                                  </button>
                                </li>
                              )}
                              <li>
                                <button
                                  onClick={() => {
                                    productHitsRef.current.bulkEdit(
                                      "bulk-publish"
                                    );
                                  }}
                                  className="font-medium text-sm flex py-1 px-3"
                                >
                                  Publish
                                </button>
                              </li>
                              <li>
                                <button
                                  onClick={() => {
                                    productHitsRef.current.bulkEdit(
                                      "bulk-unpublish"
                                    );
                                  }}
                                  className="font-medium text-sm flex py-1 px-3"
                                >
                                  Un-Publish
                                </button>
                              </li>
                              <li>
                                <button
                                  onClick={() => {
                                    productHitsRef.current.bulkEdit(
                                      "bulk-deactivate"
                                    );
                                  }}
                                  className="font-medium text-sm flex py-1 px-3"
                                >
                                  Deactivate
                                </button>
                              </li>
                              <li>
                                <button
                                  onClick={() => {
                                    productHitsRef.current.bulkEdit(
                                      "bulk-reactivate"
                                    );
                                  }}
                                  className="font-medium text-sm flex py-1 px-3"
                                >
                                  Reactivate
                                </button>
                              </li>
                              {/*<li>*/}
                              {/*  <button*/}
                              {/*    className="font-medium text-sm flex py-1 px-3"*/}
                              {/*  >*/}
                              {/*    Publish / Unpublish*/}
                              {/*  </button>*/}
                              {/*</li>*/}
                            </DropdownEditMenu>
                          </div>

                          <PrimaryButton
                            onClick={() => {
                              navigate("/products/new");
                            }}
                            disabled={disableNav}
                          >
                            Create Product
                          </PrimaryButton>
                        </div>
                      </CanView>
                    </div>
                  </CanView>

                  <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative">
                    <header className="px-5 py-4 flex h-16">
                      <h2 className="font-semibold text-slate-800 dark:text-slate-100 flex grow items-center">
                        {viewName}
                        <span className="text-slate-400 dark:text-slate-500 font-medium ml-2">
                          <CustomStates />
                        </span>
                      </h2>
                      <div
                        className={`flex items-center ${
                          disableNav ? "hidden" : ""
                        }`}
                      >
                        <Autocomplete
                          searchClient={searchClient}
                          placeholder="Search products"
                          detachedMediaQuery="none"
                          openOnFocus
                        />
                      </div>
                    </header>

                    <div>
                      <div className="overflow-x-auto">
                        <ProductHits
                          ref={productHitsRef}
                          view={currentView}
                          headers={headers}
                          setDisableNav={setDisableNav}
                          disableNav={disableNav}
                          customUpdated={location.state?.updated}
                          customCreated={location.state?.created}
                          setSelectedSkuCount={setSelectedSkuCount}
                          setDocumentModalOpen={setDocumentModalOpen}
                          setImageModalOpen={setImageModalOpen}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Modal */}
      <DocumentModal
        skuData={documentModalOpen}
        setDocumentModalOpen={setDocumentModalOpen}
        refreshDocumentCounts={(skuIds) =>
          productHitsRef.current?.refreshDocumentCounts(skuIds)
        }
      />

      {/* Product Image Modal */}
      <ProductImageModal
        skuData={imageModalOpen}
        setImageModalOpen={setImageModalOpen}
        updateImageOverride={(skuId, imageUrl) =>
          productHitsRef.current?.updateImageOverride(skuId, imageUrl)
        }
      />
    </InstantSearch>
  );
};

export default ProductsListing;
